import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuButton,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Skeleton,
    Switch,
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tooltip,
    Tr
} from '@chakra-ui/react'
import { Icon, Pagination } from 'components/atoms'
import { Status } from 'components/atoms/Status/Status'
import { NotExist } from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import React from 'react'
import { TableBuildActionsIconDefault } from './constants'
import { TableBuildProps } from './interfaces'
import { TableBuildActionsType } from './type'

const STRIPED_ROW_BACKGROUND_COLOR = '#F4F4F5'
const DEFAULT_ROW_BACKGROUND_COLOR = '#FFFFFF'

export const TableBuild: React.FC<TableBuildProps<string>> = ({
    header,
    body,
    loading,
    pagination,
    action,
    error,
    ordenation,
    onChangePage,
    onChangeLimit,
    onAction,
    onOrderBy
}) => {
    const positionSort = (
        previous: { position: number },
        current: { position: number }
    ) => previous.position - current.position

    const headerSorted = header.sort(positionSort)

    const limits = ['5', '10', '20', '30', '40', '50']

    const handlePage = (page: number) => {
        if (onChangePage) {
            onChangePage(page)
        }
    }

    const handleLimit = (limit: string) => {
        if (onChangeLimit) {
            onChangeLimit(+limit)
        }
    }

    const handleOrderBy = (order: string) => {
        if (onOrderBy) {
            onOrderBy(order)
        }
    }

    const handleAction = (
        type: TableBuildActionsType,
        index: number | string,
        value?: string | number | boolean
    ) => {
        if (onAction) {
            onAction(type, index, value)
        }
    }

    const getRowBackgroundColor = (
        rowBackgroundColor: string | undefined,
        index: number
    ): string => {
        if (rowBackgroundColor) {
            return rowBackgroundColor
        }

        return index % 2 === 0
            ? STRIPED_ROW_BACKGROUND_COLOR
            : DEFAULT_ROW_BACKGROUND_COLOR
    }

    const { isBackoffice } = useAuthContext()

    return (
        <>
            {body?.length || loading?.status ? (
                <Box
                    data-testid="table-box"
                    bgColor="white"
                    pt={3}
                    borderRadius={6}
                >
                    <TableContainer
                        data-testid="table-container"
                        overflowY="auto"
                        maxH={{
                            base: 'calc(100vh - 310px)',
                            md: 'calc(100vh - 260px)'
                        }}
                    >
                        <Table data-testid="table" variant="simple" border={0}>
                            <Thead
                                data-testid="table-header"
                                position="sticky"
                                zIndex={10}
                                top="0"
                                background="white"
                            >
                                <Tr data-testid="table-header-row-0">
                                    {headerSorted.map(
                                        ({ name, text, order }) => (
                                            <Th
                                                data-testid={`table-header-${name}-0`}
                                                key={`table-header-${name}-0`}
                                                onClick={() =>
                                                    order && handleOrderBy(name)
                                                }
                                                tabIndex={order ? 0 : -1}
                                                role={order ? 'button' : 'cell'}
                                                aria-pressed={
                                                    order ? 'true' : 'false'
                                                }
                                                textTransform="none"
                                                fontSize="17px"
                                                fontWeight="bold"
                                                letterSpacing="normal"
                                                color="inherit"
                                                w={
                                                    headerSorted.slice(-1)[0]
                                                        ?.name === name
                                                        ? 'full'
                                                        : 'auto'
                                                }
                                            >
                                                <Box
                                                    data-testid={`table-header-div-${name}-0`}
                                                    key={`table-header-div-${name}-0`}
                                                    display="flex"
                                                    alignItems="center"
                                                >
                                                    {text}

                                                    {order && (
                                                        <IconButton
                                                            data-testid={`table-header-order-${name}-0`}
                                                            key={`table-header-order-${name}-0`}
                                                            aria-label={
                                                                ordenation?.tipoOrdenacao ||
                                                                ''
                                                            }
                                                            colorScheme="transparent"
                                                            color={
                                                                ordenation?.ordenacao ===
                                                                name
                                                                    ? 'inherit'
                                                                    : 'gray.300'
                                                            }
                                                            icon={
                                                                <Icon
                                                                    data-testid={`table-header-order-icon-${name}-0`}
                                                                    key={`table-header-order-icon-${name}-0`}
                                                                    name={
                                                                        ordenation?.tipoOrdenacao ===
                                                                        'ASC'
                                                                            ? 'carret-up'
                                                                            : 'carret-down'
                                                                    }
                                                                />
                                                            }
                                                        />
                                                    )}
                                                </Box>
                                            </Th>
                                        )
                                    )}

                                    {action && (
                                        <Th
                                            data-testid="table-header-action-0"
                                            key="table-header-action-0"
                                            textAlign="center"
                                            textTransform="none"
                                            fontSize="17px"
                                            fontWeight="bold"
                                            letterSpacing="normal"
                                            color="inherit"
                                        >
                                            Ação
                                        </Th>
                                    )}
                                </Tr>
                            </Thead>

                            {loading?.status ? (
                                <Tbody data-testid="loading">
                                    {Array.from({
                                        length: pagination?.limit || 10
                                    }).map((_, row) => (
                                        <Tr key={`loader-${row}`} height="49px">
                                            {loading.loader.map((attr, col) => (
                                                <Td
                                                    key={`loader-${row}-${col}`}
                                                >
                                                    <Skeleton {...attr} />
                                                </Td>
                                            ))}
                                        </Tr>
                                    ))}
                                </Tbody>
                            ) : (
                                <Tbody data-testid="table-body">
                                    {body?.map((items, index) => {
                                        const backgroundColor =
                                            getRowBackgroundColor(
                                                items.rowBackgroundColor,
                                                index
                                            )

                                        return (
                                            <Tr
                                                data-testid={`table-body-row-${index}`}
                                                key={`table-body-row-${index}`}
                                            >
                                                {headerSorted.map(
                                                    (
                                                        { name, alignTo },
                                                        col
                                                    ) => (
                                                        <Td
                                                            data-testid={`table-body-row-${index}-${name}-${col}`}
                                                            key={`table-body-row-${index}-${name}-${col}`}
                                                            textAlign={
                                                                alignTo ??
                                                                'left'
                                                            }
                                                            style={{
                                                                backgroundColor
                                                            }}
                                                        >
                                                            {name ===
                                                                'status' &&
                                                            window.location.href.includes(
                                                                'gestao-taxas'
                                                            ) ? (
                                                                <Status
                                                                    status={
                                                                        items[
                                                                            name
                                                                        ] as any
                                                                    }
                                                                />
                                                            ) : (
                                                                items[name]
                                                            )}
                                                        </Td>
                                                    )
                                                )}

                                                {action && (
                                                    <Td
                                                        data-testid={`table-body-row-action-${index}`}
                                                        key={`table-body-row-action-${index}`}
                                                        textAlign="center"
                                                        style={{
                                                            backgroundColor
                                                        }}
                                                    >
                                                        {action.map(
                                                            ({
                                                                type,
                                                                icon,
                                                                propsTooltip,
                                                                disableTooltip
                                                            }) =>
                                                                type ===
                                                                'SWITCH' ? (
                                                                    <Tooltip
                                                                        {...propsTooltip}
                                                                        data-testid={`table-body-tooltip-${index}`}
                                                                        key={`table-body-tooltip-${index}`}
                                                                        isDisabled={
                                                                            disableTooltip ===
                                                                            undefined
                                                                                ? true
                                                                                : disableTooltip
                                                                        }
                                                                    >
                                                                        <Switch
                                                                            data-testid={`table-body-action-${type}-${index}`}
                                                                            key={`table-body-action-${type}-${index}`}
                                                                            colorScheme="primary"
                                                                            size="md"
                                                                            defaultChecked={
                                                                                items?.status ===
                                                                                'ATIVO'
                                                                            }
                                                                            onChange={e =>
                                                                                handleAction(
                                                                                    type,
                                                                                    items.id ??
                                                                                        index,
                                                                                    e
                                                                                        .target
                                                                                        .checked
                                                                                )
                                                                            }
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip
                                                                        {...propsTooltip}
                                                                        data-testid={`table-body-tooltip-${index}-${type}`}
                                                                        key={`table-body-tooltip-${index}-${type}`}
                                                                        isDisabled={
                                                                            disableTooltip ===
                                                                            undefined
                                                                                ? true
                                                                                : disableTooltip
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            data-testid={`table-body-action-${type}-${index}`}
                                                                            key={`table-body-action-${type}-${index}`}
                                                                            colorScheme="transparent"
                                                                            color="inherit"
                                                                            aria-label={
                                                                                type
                                                                            }
                                                                            icon={
                                                                                <Icon
                                                                                    name={
                                                                                        icon ||
                                                                                        TableBuildActionsIconDefault[
                                                                                            type
                                                                                        ]
                                                                                    }
                                                                                />
                                                                            }
                                                                            onClick={() =>
                                                                                handleAction(
                                                                                    type,
                                                                                    items.id ??
                                                                                        index
                                                                                )
                                                                            }
                                                                            zIndex={
                                                                                0
                                                                            }
                                                                        />
                                                                    </Tooltip>
                                                                )
                                                        )}
                                                    </Td>
                                                )}
                                            </Tr>
                                        )
                                    })}
                                </Tbody>
                            )}
                        </Table>
                    </TableContainer>

                    {pagination && (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            px={4}
                        >
                            <Menu matchWidth>
                                {({ isOpen }) => (
                                    <>
                                        <MenuButton
                                            data-testid="pagination-button-limit"
                                            as={Button}
                                            colorScheme="off-white"
                                            color="inherit"
                                            size="md"
                                            shadow="md"
                                            gap={2}
                                            rightIcon={
                                                <Icon
                                                    name={
                                                        isOpen
                                                            ? 'chevron-down'
                                                            : 'chevron-up'
                                                    }
                                                />
                                            }
                                        >
                                            {pagination.limit}
                                        </MenuButton>

                                        <MenuList
                                            data-testid="pagination-limit-list"
                                            border={0}
                                            shadow="md"
                                            minW="inherit"
                                            position="sticky"
                                            zIndex={12}
                                        >
                                            <MenuOptionGroup
                                                data-testid="pagination-group"
                                                type="radio"
                                                border={0}
                                                shadow="md"
                                                defaultValue="10"
                                                onChange={value =>
                                                    handleLimit(value as string)
                                                }
                                            >
                                                {limits.map(limit => (
                                                    <MenuItemOption
                                                        data-testid={`pagination-limit-${limit}`}
                                                        key={`pagination-limit-${limit}`}
                                                        value={limit}
                                                    >
                                                        {limit}
                                                    </MenuItemOption>
                                                ))}
                                            </MenuOptionGroup>
                                        </MenuList>
                                    </>
                                )}
                            </Menu>

                            <Pagination
                                py={4}
                                pages={pagination.totalPages}
                                currentPage={pagination.page}
                                loading={!pagination.page}
                                changePage={handlePage}
                            />
                        </Box>
                    )}
                </Box>
            ) : (
                <NotExist
                    isBackoffice={isBackoffice}
                    {...error}
                    paddingBlock={20}
                    height="100%"
                />
            )}
        </>
    )
}